import { useCallback, useEffect, useState } from 'react';
import { MdOutlineFileDownload, MdOutlinePeopleAlt, MdChevronLeft, MdChevronRight } from 'react-icons/md';
import { useToast } from '@podus/ui';
import StatusBadge from '@/components/StatusBadge';
import EmptyState from '@/components/EmptyState';
import { api, ApiError, downloadCsv } from '@/lib/api';
import { formatDate } from '@/lib/format';
import type { MaskedReferral, Pagination, ReferralStatus } from '@/types';

type SortField = 'createdAt' | 'activatedAt' | 'status';

const TABS: { label: string; value: ReferralStatus | 'ALL' }[] = [
    { label: 'All', value: 'ALL' },
    { label: 'Confirmed', value: 'CONFIRMED' },
    { label: 'Under review', value: 'PENDING_REVIEW' },
];

/**
 * The ambassador's referred-users table (FR-25).
 *
 * Identifiers arrive already masked from the API — this page has no access to
 * a referred user's real email or phone number, by design (FR-29).
 */
export default function Referrals() {
    const toast = useToast();

    const [rows, setRows] = useState<MaskedReferral[]>([]);
    const [pagination, setPagination] = useState<Pagination | null>(null);
    const [page, setPage] = useState(1);
    const [tab, setTab] = useState<ReferralStatus | 'ALL'>('ALL');
    const [sort, setSort] = useState<SortField>('createdAt');
    const [order, setOrder] = useState<'asc' | 'desc'>('desc');
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams({
                page: String(page),
                limit: '20',
                sort,
                order,
                ...(tab !== 'ALL' ? { status: tab } : {}),
            });
            const res = await api.get<{ data: MaskedReferral[]; pagination: Pagination }>(
                `/ambassador/me/referrals?${query.toString()}`,
            );
            setRows(res.response.data);
            setPagination(res.response.pagination);
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not load your referrals');
        } finally {
            setLoading(false);
        }
    }, [page, tab, sort, order, toast]);

    useEffect(() => {
        void load();
    }, [load]);

    const toggleSort = (field: SortField) => {
        if (sort === field) {
            setOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setSort(field);
            setOrder('desc');
        }
        setPage(1);
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            await downloadCsv('/ambassador/me/referrals/export', 'podus-referrals.csv');
        } catch (err) {
            toast.error(err instanceof ApiError ? err.message : 'Could not download your referrals');
        } finally {
            setExporting(false);
        }
    };

    const sortIndicator = (field: SortField) => (sort === field ? (order === 'asc' ? '↑' : '↓') : '');

    return (
        <div className="mx-auto max-w-6xl px-4 py-6 lg:px-8 lg:py-8">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h2 className="text-[22px] font-bold text-secondary">My referrals</h2>
                    <p className="mt-1 text-[13px] text-[#747474]">
                        Everyone who signed up through your link. Identities are masked to protect their privacy.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={handleExport}
                    disabled={exporting || rows.length === 0}
                    className="flex items-center gap-2 rounded-lg border border-[#E0E0E0] bg-white px-4 py-2.5 text-[13px] font-semibold text-secondary transition-colors hover:bg-[#f5f5f5] disabled:opacity-50"
                >
                    <MdOutlineFileDownload size={18} />
                    {exporting ? 'Preparing…' : 'Export CSV'}
                </button>
            </div>

            <div className="mt-5 flex items-center gap-1 border-b border-[#EEEEEE]">
                {TABS.map((item) => (
                    <button
                        key={item.value}
                        type="button"
                        onClick={() => {
                            setTab(item.value);
                            setPage(1);
                        }}
                        className={`-mb-px border-b-2 px-3 py-2.5 text-[14px] transition-colors ${
                            tab === item.value
                                ? 'border-primary font-bold text-secondary'
                                : 'border-transparent text-[#9a9a9a] hover:text-[#6b6b6b]'
                        }`}
                    >
                        {item.label}
                    </button>
                ))}
            </div>

            <div className="mt-5">
                {loading ? (
                    <div className="flex flex-col gap-2">
                        {Array.from({ length: 5 }).map((_, index) => (
                            <div key={index} className="skeleton-shimmer h-14 w-full rounded-lg" />
                        ))}
                    </div>
                ) : rows.length === 0 ? (
                    <EmptyState
                        Icon={MdOutlinePeopleAlt}
                        title="No referrals here yet"
                        description="Once someone signs up through your link, they'll appear in this table with their signup and activation dates."
                    />
                ) : (
                    <>
                        <div className="overflow-x-auto rounded-xl border border-[#E0E0E0] bg-white">
                            <table className="w-full min-w-[620px] text-left">
                                <thead>
                                    <tr className="border-b border-[#EEEEEE] text-[12px] uppercase tracking-wide text-[#9a9a9a]">
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Referred user
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            <button
                                                type="button"
                                                onClick={() => toggleSort('createdAt')}
                                                className="uppercase tracking-wide"
                                            >
                                                Signup date {sortIndicator('createdAt')}
                                            </button>
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            <button
                                                type="button"
                                                onClick={() => toggleSort('status')}
                                                className="uppercase tracking-wide"
                                            >
                                                Status {sortIndicator('status')}
                                            </button>
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            <button
                                                type="button"
                                                onClick={() => toggleSort('activatedAt')}
                                                className="uppercase tracking-wide"
                                            >
                                                Activated {sortIndicator('activatedAt')}
                                            </button>
                                        </th>
                                        <th scope="col" className="px-5 py-3 font-semibold">
                                            Country
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((row) => (
                                        <tr key={row.id} className="border-b border-[#f5f5f5] last:border-0">
                                            <td className="px-5 py-3.5 text-[14px] font-medium text-secondary">
                                                {row.identifier}
                                            </td>
                                            <td className="px-5 py-3.5 text-[14px] text-[#4E4D4D]">
                                                {formatDate(row.signupDate)}
                                            </td>
                                            <td className="px-5 py-3.5">
                                                <StatusBadge status={row.status} />
                                            </td>
                                            <td className="px-5 py-3.5 text-[14px] text-[#4E4D4D]">
                                                {formatDate(row.activationDate)}
                                            </td>
                                            <td className="px-5 py-3.5 text-[14px] text-[#4E4D4D]">
                                                {row.country ?? '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {pagination && pagination.totalPages > 1 && (
                            <div className="mt-4 flex items-center justify-between">
                                <p className="text-[13px] text-[#747474]">
                                    Page {pagination.page} of {pagination.totalPages} · {pagination.total} total
                                </p>
                                <div className="flex items-center gap-2">
                                    <button
                                        type="button"
                                        title="Previous page"
                                        disabled={page <= 1}
                                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E0E0E0] text-secondary transition-colors hover:bg-[#f5f5f5] disabled:opacity-40"
                                    >
                                        <MdChevronLeft size={18} />
                                    </button>
                                    <button
                                        type="button"
                                        title="Next page"
                                        disabled={page >= pagination.totalPages}
                                        onClick={() => setPage((p) => p + 1)}
                                        className="flex h-9 w-9 items-center justify-center rounded-lg border border-[#E0E0E0] text-secondary transition-colors hover:bg-[#f5f5f5] disabled:opacity-40"
                                    >
                                        <MdChevronRight size={18} />
                                    </button>
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
